import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import debug from 'debug'
import posthog from 'posthog-js'
import { isBrowser } from '../../utils/is.browser'
import { setCompanyId } from '../company'
import { AppDispatch, State } from '../index'

const logger = debug('store:auth')

export const authSlice = createSlice({
  initialState: {
    authenticated: false as boolean,
    email: undefined as string,
    initialized: false as boolean,
    roles: [] as string[],
  },
  name: 'auth',

  reducers: {
    init: (state, action: PayloadAction<{
      email: string | false
      roles?: string[]
    }>) => {
      const roles = action.payload.roles || []
      const email = action.payload.email

      if (email) {
        localStorage.setItem('email', email)
        localStorage.setItem('roles', JSON.stringify(roles))
      }

      return {
        authenticated: Boolean(email),
        email: email !== false ? email : undefined,
        initialized: true,
        roles,
      }
    },
    login: (state, action: PayloadAction<{
      authenticated: boolean
      email: string
      roles?: string[]
    }>) => {
      const { authenticated, email, roles} = action.payload
      return {
        authenticated,
        email: email ? email : undefined,
        initialized: true,
        roles: roles || [],
      }
    },
    removeToken: () => {
      return {
        authenticated: false,
        email: undefined,
        initialized: true,
        roles: [],
      }
    },
    setRoles: (state, action: PayloadAction<string[]>) => {
      const roles = action.payload || []

      return {
        ...state,
        roles,
      }
    },
    triggerLogin: (state) => {
      return {
        ...state,
        authenticated: true,
      }
    },
  },
})

export const { triggerLogin, init, setRoles, removeToken } = authSlice.actions

export const logout = createAsyncThunk<void, void, {
  dispatch: AppDispatch
  state: State
}>(
  'auth/logout',
  async (payload, thunkAPI) => {
    thunkAPI.dispatch(removeToken())
    thunkAPI.dispatch(setCompanyId())

    if (!isBrowser()) {
      logger('DANGER!!! called logout in SSR')

      return
    }

    logger('force logout')

    try {
      posthog.reset()
    } catch (error) {
      logger('failed to reset posthog %o', error)
    }

    // clear synchronously first so it still runs during a beforeunload dispatch
    localStorage.clear()
    sessionStorage.clear()

    // keepalive request clears the httpOnly auth cookies even if the page is
    // being unloaded (idle/reopen logout); the navigation below covers the
    // normal case where the redirect must be atomic with the cookie deletion.
    void fetch('/api/auth/logout', {
      credentials: 'include',
      keepalive: true,
      method: 'POST',
    }).catch(error => logger('failed to clear auth cookies %o', error))

    // full-page navigation to the logout route: it deletes every auth cookie
    // and redirects home in one response, so nothing can abort it mid-flight
    window.location.assign('/api/auth/logout')
  })
